import React, { useEffect, useMemo, useState } from "react";
import { useFormSubmission } from "@hooks";
import currency from "currency.js";
import { helpers, Enums, Constants } from "@shared";
import UpsertPayor from "./upsert-payor";
import PropTypes from 'prop-types';
import { XCircleIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";

const InvoicePayors = ({ accountPayors, done, invoice, invoicePayors, nextEnrollmentDate, idProductType, account }) => {

    const { dueDate: invoiceDueDate, sunboundTotal = null } = invoice || {};
    const baseDate = !invoice ? nextEnrollmentDate : invoiceDueDate;
    const {
        idPayor: idCurrentPayor,
        orgDays: { dueDay: orgDueDay },
        potDeadline
    } = account;

    const { clearFormState, hasFormError, formError } = useFormSubmission();
    const [payors, setPayors] = useState([]);
    const [currentPayor, setCurrentPayor] = useState(null);
    const [selectionPayors, setSelectionPayors] = useState([]);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        const { adjustedPayors, availPayors } = processPayorsLists(accountPayors, invoicePayors);
        setPayors(adjustedPayors);
        setSelectionPayors(availPayors);
    }, []);

    const processPayorsLists = (accountPayors, invoicePayors) => {
        const availPayorsFilter = accountPayors.filter(ap => {
            const { isGuarantor, isActive } = ap;

            return !isGuarantor && isActive;
        });
        const availPayors = availPayorsFilter.map(avail => {
            const { payor, ...rest } = avail;
            const { fullName, email } = payor;
            return { ...rest, fullName, email };
        });
        const adjustedPayors = invoicePayors.map(p => ({ ...p }));

        adjustedPayors.forEach((p) => {
            const index = availPayors.findIndex((sp) => sp.idPayor === p.idPayor);
            if (index !== -1) {
                availPayors.splice(index, 1);
            }
        });

        adjustedPayors.sort((a) => a.idPayor === idCurrentPayor ? -1 : 1);

        return {
            adjustedPayors,
            availPayors
        };
    };

    const upsert = ({ idPayor, idInvitation, email, fullName, payAmount, payPercentage, dueDay, amountType, alreadySelected, paid }) => {

        const adjustedDueDay = idProductType === Enums.ProductType.SunboundPay ? potDeadline : (dueDay ?? orgDueDay);

        const newPayor = {
            idPayor,
            idInvitation,
            email,
            fullName,
            payAmount: currency(payAmount).value,
            payPercentage: currency(payPercentage, { precision: 4 }).value,
            dueDay: adjustedDueDay,
            amountType,
            alreadySelected,
            paid
        };

        const newPayors = [...payors];
        newPayors.push(newPayor);

        calculate(newPayors);
        setPayors(newPayors);
        setCurrentPayor(null);
        clearFormState();
        setEditing(false);
    };

    const calculate = (newPayors) => {
        if (sunboundTotal === null) return;

        let rest = currency(sunboundTotal);

        newPayors.forEach(ap => {
            const { payAmount, isGuarantor = false } = ap;
            if (!isGuarantor)
                rest = rest.subtract(payAmount);
        });
        const guarantor = findGuarantor(newPayors);

        guarantor.payAmount = rest.value;
        guarantor.payPercentage = helpers.percentage(guarantor.payAmount, sunboundTotal);
    };

    const cancelEditing = () => {

        if (currentPayor !== null) {
            if (currentPayor.alreadySelected) {
                const newPayors = [...payors];
                newPayors.push({ ...currentPayor });
                calculate(newPayors);
                setPayors(newPayors);

            } else if (currentPayor.idPayor) {
                const newSelectionPayors = [...selectionPayors];
                newSelectionPayors.push({ ...currentPayor });
                setSelectionPayors(newSelectionPayors);
            }
        }

        setCurrentPayor(null);
        setEditing(false);
    };

    const removePayor = (index) => {

        const newPayors = [...payors];
        const removed = newPayors.splice(index, 1);
        const [{ idPayor }] = removed;
        if (idPayor != null) {
            const payor = accountPayors.find(p => p.idPayor === idPayor);
            const newAvailable = [...selectionPayors];
            newAvailable.push({ ...payor });
            setSelectionPayors(newAvailable);
        }
        calculate(newPayors);
        setPayors(newPayors);
    };

    const editPayor = (index) => {
        const newPayors = [...payors];
        const removed = newPayors.splice(index, 1);
        calculate(newPayors); setCurrentPayor({ ...removed[0], alreadySelected: true });
        setPayors(newPayors);
        setEditing(true);
    };

    const selectPayor = (index) => {
        const newSelectionPayors = [...selectionPayors];
        const [add] = newSelectionPayors.splice(index, 1);

        setSelectionPayors(newSelectionPayors);
        let { payAmount, payPercentage } = add;
        const { amountType } = add;
        if (amountType === Enums.AmountType.Percentage)
            payAmount = helpers.toFixedDown(currency(payPercentage).multiply(sunboundTotal), 2);
        else if (amountType === Enums.AmountType.Amount)
            payPercentage = helpers.toFixedDown(helpers.percentage(payAmount, sunboundTotal), 2);

        setCurrentPayor({ ...add, alreadySelected: false, payAmount, payPercentage });
        setEditing(true);
    };

    const payorsConfiguration =
        payors.map((p, index) => {
            const {
                amountType,
                payPercentage,
                payAmount,
                paid,
                fullName,
                email,
                idPayor,
                dueDay,
                isGuarantor
            } = p;

            let displayContribution = null;
            if (sunboundTotal === null) {
                const contribution = amountType === Enums.AmountType.Percentage ? `${helpers.printPercentage(payPercentage)}%` : currency(payAmount).format();
                displayContribution = (
                    <span className="w-6/12 text-center self-center">
                        {
                            !isGuarantor && contribution
                        }
                    </span>
                );
            } else {
                displayContribution = (
                    <>
                        <span className="w-3/12 text-right self-center">{helpers.printPercentage(payPercentage)}%</span>
                        <span className="w-3/12 text-right self-center">{currency(payAmount).format()}</span>
                    </>
                );
            }

            const nameLabel = idPayor === idCurrentPayor ? <b>You</b> : fullName;

            const hasPayments = !!paid && currency(paid).intValue > 0;
            return (
                <div key={email} className="flex flex-row">
                    <div className="flex flex-row bg-sb-bg rounded-lg border-2 border-gray-200 w-full p-2  px-3 space-x-3">
                        <span className="w-4/12 text-left self-center">{nameLabel}</span>
                        <span className="whitespace-nowrap w-2/12 text-left self-center">{helpers.compoundDay(baseDate, dueDay).toFormat(`MMMM d`)}</span>
                        {displayContribution}
                        <div className="w-20">
                            {
                                !isGuarantor &&
                                <button
                                    type="button"
                                    className="btn btn-outline-primary p-1 m-1 text-sm rounded h-7 self-center"
                                    disabled={editing}
                                    onClick={() => {
                                        editPayor(index);
                                    }}>Edit</button>
                            }
                        </div>
                    </div>
                    <div className="flex text-center self-center w-10 bg-white align-middle">
                        <button
                            type="button"
                            className="background-transparent underline font-bold text-white text-lg outline-none focus:outline-none"
                            disabled={isGuarantor === true || hasPayments || editing}
                            onClick={() => {
                                removePayor(index);
                            }}>
                            <XCircleIcon className={classNames(`h-10`, { "fill-blue-on": !isGuarantor && !hasPayments && !editing }, { "fill-gray-400": isGuarantor === true || hasPayments || editing })} />
                        </button>
                    </div>
                </div>
            );
        });

    const next = () => {
        done(payors);
    };

    const payorSelector = (selectionPayors, editing) => (
        <>
            {
                selectionPayors.map((p, index) => {
                    const { email, fullName, payAmount, payPercentage, amountType, dueDay } = p;

                    return (
                        <div key={email} className="bg-gray-200 w-full p-5">
                            <div className="flex flex-col gap-y-3">
                                <div className="flex flex-row">
                                    <div className="flex flex-col w-1/2">
                                        <span className="font-bold">{fullName}</span>
                                        <span className="">{email}</span>
                                    </div>
                                    <div className="flex w-1/2 justify-end items-center">
                                        <button type="button" className="btn-outline-primary h-8 w-12 border-2" disabled={editing} onClick={() => selectPayor(index)}>Add</button>
                                    </div>
                                </div>
                                {
                                    amountType != null && dueDay &&
                                    <div className="flex w-full justify-evenly">
                                        {
                                            amountType === Enums.AmountType.Percentage &&
                                            <span className="bg-white px-3 py-1 rounded-md">{`${helpers.printPercentage(payPercentage)} %`}</span>
                                        }
                                        {
                                            amountType === Enums.AmountType.Amount &&
                                            <span className="bg-white px-3 py-1 rounded-md">{currency(payAmount).format()}</span>
                                        }
                                        {
                                            dueDay &&
                                            <>
                                                <div className="w-1 bg-gray-300"></div>
                                                <span className="bg-white px-3 py-1 rounded-md w-1/2 text-center">{`On the ${helpers.dayOrdinal(dueDay)}`}</span>
                                            </>
                                        }
                                    </div>
                                }
                            </div>
                        </div>);
                })
            }
        </>
    );

    const findGuarantor = (payors) => {
        const guarantor = payors.find(p => p.isGuarantor);
        return guarantor;
    };

    const getLimits = (sunboundTotal, payors) => {
        if (sunboundTotal === null || payors.length === 0) {
            return null;
        }
        const guarantor = findGuarantor(payors);
        return {
            max: currency(guarantor.payAmount).subtract(guarantor.paid).value,
            min: guarantor.paid || Constants.System.MIN_PAYMENT_AMOUNT
        };
    };

    const limits = useMemo(() => getLimits(sunboundTotal, payors), [payors, sunboundTotal]);

    const allocatable = useMemo(() => {
        const guarantor = findGuarantor(payors);
        if (!guarantor) return null;

        const { payAmount, paid } = guarantor;
        return (
            <div className="flex flex-col gap-y-3 w-full">
                <span className="text-lg font-semibold">{`Allocatable ${currency(payAmount).subtract(paid).format()}`}</span>
            </div>

        );
    }, [payors]);


    const creatingPayor = () => {
        setEditing(true);
        setCurrentPayor(null);
    };

    return (
        <div className="page-content-wrapper gap-y-6 px-4">
            <h1 className="page-header">Add other payors</h1>
            <span className="page-subheader">Add other payors to help pay down the balance each month</span>
            {allocatable}
            {
                payors.length !== 0 &&
                <>

                    <div className="flex flex-col gap-y-3 w-full">
                        {payorsConfiguration}
                    </div>
                </>
            }
            {payorSelector(selectionPayors, editing)}
            {
                !editing &&
                <div className="w-full">
                    <button
                        type="button"
                        className="btn btn-outline-primary rounded-full p-3 border-2"
                        onClick={creatingPayor}>Create New Payor</button>
                </div>
            }
            {
                editing &&
                <UpsertPayor
                    sunboundTotal={sunboundTotal}
                    upsert={upsert}
                    count={payors.length}
                    limits={limits}
                    cancelEditing={cancelEditing}
                    payor={currentPayor}
                    accountPayors={accountPayors} />
            }
            {
                hasFormError &&
                <p className="text-red-500 text-lg">{formError}</p>
            }
            <div className="mt-4 w-full self-end">
                <button className="btn btn-primary" disabled={editing} onClick={next}>Continue</button>
            </div>
        </div>
    );

};

InvoicePayors.propTypes = {
    done: PropTypes.func.isRequired,
    accountPayors: PropTypes.array.isRequired,
    invoice: PropTypes.object,
    invoicePayors: PropTypes.array.isRequired,
    nextEnrollmentDate: PropTypes.instanceOf(Date),
    idProductType: PropTypes.number.isRequired,
    account: PropTypes.object.isRequired
};

export default InvoicePayors;
